import {CacheManager, cacheTypeEnumerate} from '@aofl/cache-manager';
import {ServerTime} from '@aoflmkt/server-time';
import {isMobile} from '@aoflmkt/is-mobile';
import {ApiService} from './api-service';
import {CampaignService} from './campaign-service';
import {apis, cacheNamespaces, cookiesConsent, environment, eventNames, cookies} from './constants-enumerate';
import {marketingTrackerQueue} from './marketing-tracker-queue';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/browser';
import {uuid} from '@aofl/uuid';
import {userInfoSdo} from './sdo-user-info';
import packageJson from '../../package.json';

const cacheManager = new CacheManager(cacheNamespaces.CAMPAIGN_PIXELS_LOG, cacheTypeEnumerate.LOCAL);

/**
 * Service to fire pixels using an iframe
 * @class
 */
class PixelService {
  /**
   *
   * @param {String} event (landing|conversion|sign-up)
   * @param {Object} [data] Optional GTM payload (e.g. cardName, phoneNumber, zipCode); merged into eventData
   * @return {Promise}
   */
  static async fire(event, data) {
    if (Cookies.get(cookies.WDIO_SUPPRESS_PIXELS)) return;
    await PixelService.fireInternalPixel(event);
    if (environment.IN_APP) return;

    if (Cookies.get(cookiesConsent.ANALYTICS) === 'true') {
      await PixelService.fireCampaignPixel(event);
    }

    await PixelService.fireGlobalPixel(event, data);
  }

  /**
   *
   * @param {String} event
   * @return {Function}
   */
  static trackEvent(action, event, type) {
    const campaignId = CampaignService.getCampaignInfo()?.id;

    const trackerPayload = {
      event_type: `${eventNames.PIXEL_FIRE}-${action}`,
      payload: {
        event,
        type,
        campaignId
      }
    };

    return marketingTrackerQueue.enqueueEvent(trackerPayload);
  }

  /**
   *
   * @param {String} event
   * @param {Object} [data] Optional GTM payload (e.g. cardName, phoneNumber, zipCode); merged into eventData
   * @return {Promise}
   */
  static async fireGlobalPixel(event, data) {
    if (navigator?.globalPrivacyControl) return;
    const {globalPixels} = await import('./__config/pixels');

    if (globalPixels()[event]) {
      PixelService.trackEvent('before', event, 'global');
      await PixelService.attachPixel(globalPixels()[event]);
      PixelService.trackEvent('after', event, 'global');
    }
    await PixelService.setGtmEventMetadata(event, data);
  }

  /**
   *
   * @param {String} event
   * @return {Promise}
   */
  static async fireInternalPixel(event) {
    if (navigator?.globalPrivacyControl) return;
    const {internalPixels} = await import('./__config/pixels');

    if (!internalPixels()[event]) return;

    const pixelsInternal = internalPixels()[event];
    PixelService.trackEvent('before', event, 'internal');

    for (let i = 0; i < pixelsInternal.length; i++) {
      try {
        await fetch(pixelsInternal[i], { // eslint-disable-line
          method: 'GET',
          headers: {
            'Content-type': 'application/json;charset=UTF-8',
            'Access-Control-Allow-Origin': '*',
          },
          mode: 'no-cors'
        });
      } catch (err) {
        Sentry.captureException(err);
      }
    }

    PixelService.trackEvent('after', event, 'internal');
  }

  /**
   *
   * @param {String} event
   * @return {Promise}
   */
  static async fireCampaignPixel(event) {
    if (navigator?.globalPrivacyControl) return;
    const campaignInfo = CampaignService.getCampaignInfo();
    if (campaignInfo.id !== null && !PixelService.hasPixelFire(campaignInfo.id, event)) {
      const response = await ApiService.resolvePixelInfo(event);

      if (response.payload && response.payload.pixel) {
        PixelService.trackEvent('before', event, 'campaign');
        await PixelService.attachPixel(response.payload.pixel);
        PixelService.trackEvent('after', event, 'campaign');
        PixelService.logPixel(campaignInfo.id, event);
      }
    }
  }

  /**
   *
   * @param {String} campaignId
   * @param {String} event
   * @return {Boolean}
   */
  static hasPixelFire(campaignId, event) {
    const pixelLog = PixelService.getPixelLog();
    let hasPixelFire = false;
    const expiration = ServerTime.getAdjustedTime(apis.MARKETING) - (3600*24*30);

    if (pixelLog[campaignId]
      && pixelLog[campaignId][event]
      && pixelLog[campaignId][event] > expiration
    ) {
      hasPixelFire = true;
    }
    return hasPixelFire;
  }

  /**
   *
   * @return {Object}
   */
  static getPixelLog() {
    const campaignPixels = cacheManager.getItem('pixelLog');
    let pixelLog = {};

    if (campaignPixels) {
      try {
        pixelLog = JSON.parse(campaignPixels);
      } catch (e) {}
    }
    return pixelLog;
  }

  /**
   *
   * @param {String} campaignId
   * @param {String} event
   */
  static logPixel(campaignId, event) {
    const pixelLog = PixelService.getPixelLog();
    pixelLog[campaignId] = pixelLog[campaignId] || {};
    pixelLog[campaignId][event] = ServerTime.getAdjustedTime(apis.MARKETING);
    cacheManager.setItem('pixelLog', JSON.stringify(pixelLog));
  }

  /**
   * Creates and iframe and resolves the promise
   * after the iframe has done loading
   * @param {String} pixel
   * @return {Promise}
   */
  static attachPixel(pixel) {
    return new Promise((resolve, reject) => {
      const iFrame = document.createElement('iframe');
      iFrame.width = 1;
      iFrame.height = 1;
      iFrame.frameborder = 0;
      iFrame.style.display = 'none';

      document.body.appendChild(iFrame);

      iFrame.addEventListener('load', function() {
        resolve();
      });

      iFrame.contentWindow.document.open();
      iFrame.contentWindow.document.write(pixel);
      iFrame.contentWindow.document.close();
    });
  }

  /**
   *
   * @param {String} page
   */
  static trackPageLoad(page) {
    try {
      // Google Analytics
      if (window.gtag) {
        window.gtag('set', 'page', page);
        window.gtag('send', 'pageview');
      }

      // Bing UET
      window.uetq = window.uetq || [];
      window.uetq.push('event', 'page_view', {'page_path': page});
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  /**
   *
   * @return {String}
   */
  static getSourceId() {
    const sourceInfoCookie = Cookies.get(cookies.CAMPAIGN_INFO);
    let sourceId = '';

    if (sourceInfoCookie) {
      const sourceInfo = JSON.parse(decodeURIComponent(sourceInfoCookie));
      sourceId = sourceInfo?.id;
    }

    return sourceId;
  }

  /**
   * @param {String} event
   */
  static async hashValue(string) {
    const stringToLowerCase = string.toLowerCase();
    const utf8 = new TextEncoder().encode(stringToLowerCase);
    const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((bytes) => bytes.toString(16).padStart(2, '0'))
      .join('');
    return hashHex.toLowerCase();
  }

  /**
   * @return {String}
   */
  static getDeviceType() {
    if (isMobile.tablet) return 'Tablet';
    if (environment.IS_DESKTOP) return 'Desktop';
    if (isMobile.phone) return 'Mobile';
    return 'Mobile';
  }

  /**
   * Split cardholder name into first/last for GTM (last token is last name, remainder is first).
   * @param {string} [cardName]
   * @return {{firstName: string, lastName: string}}
   */
  static namesFromCardName(cardName) {
    const trimmed = String(cardName ?? '').trim();
    if (!trimmed) return {firstName: '', lastName: ''};
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return {firstName: parts[0], lastName: ''};
    return {
      firstName: parts.slice(0, -1).join(' '),
      lastName: parts[parts.length - 1]
    };
  }

  /**
   *
   * @param {String} event
   * @param {Object} [data] GTM form fields (e.g. cardName, phoneNumber, zipCode) and other eventData keys
   */
  static async setGtmEventMetadata(event, data) {
    const elogCookie = Cookies.get(cookies.ELOG);
    const userUuid = userInfoSdo.userInfo.userUuid || '';
    const cjeCookie = Cookies.get(cookies.CJ_COOKIE);
    const externalID = cjeCookie || uuid();

    let hashedMemberExternalID = '';
    let userEmail = '';
    let hashedEmail = '';

    if (elogCookie) {
      userEmail = elogCookie;
    }

    if (userEmail) {
      hashedEmail = await this.hashValue(userEmail);
    }

    if (userUuid) {
      hashedMemberExternalID = await this.hashValue(userUuid);
    } else {
      hashedMemberExternalID = uuid();
    }

    const hasCardName = Boolean(data) && Object.prototype.hasOwnProperty.call(data, 'cardName');
    const {firstName, lastName} = hasCardName
      ? PixelService.namesFromCardName(data.cardName)
      : {firstName: data?.firstName || '', lastName: data?.lastName || ''};

    const gtmEventMetadata = {
      analyticsCookiesEnabled: Cookies.get(cookiesConsent.ANALYTICS) === 'true',
      cjeCookie,
      clientVersion: packageJson.version || '',
      conversionEventID: uuid(),
      deviceType: PixelService.getDeviceType(),
      externalID,
      fbp: Cookies.get('_fbp') || '',
      geo: Cookies.get(cookies.COUNTRY_CODE) || '',
      hashedEmail,
      hashedMemberExternalID,
      inApp: environment.IN_APP,
      isAutomatedTest: Boolean(Cookies.get(cookies.WDIO)),
      language: (() => {
        const raw = document?.documentElement?.lang || navigator?.language || '';
        return (raw.replace('_', '-').split('-')[0] || '').toLowerCase();
      })(),
      pageUrl: window?.location?.href ?? '',
      sourceTag: PixelService.getSourceId() ?? '',
      targetingCookiesEnabled: Cookies.get(cookiesConsent.TARGETING) === 'true',
      timestamp: Date.now(),
      userAgent: navigator?.userAgent ?? '',
      // Subscription Metadata
      currency: data?.currency || '',
      firstName,
      lastName,
      mobile: data?.mobile || '',
      product: data?.product || '',
      state: data?.state || '',
      value: data?.value || '',
      zip: data?.zipCode || '',
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      {
        'id': gtmEventMetadata.conversionEventID,
        event,
        'eventData': {
          analyticsCookiesEnabled: gtmEventMetadata.analyticsCookiesEnabled,
          cjeCookie: gtmEventMetadata.cjeCookie || '',
          clientVersion: gtmEventMetadata.clientVersion,
          deviceType: gtmEventMetadata.deviceType,
          externalID: gtmEventMetadata.externalID,
          hashedMemberExternalID: gtmEventMetadata.hashedMemberExternalID,
          fbp: gtmEventMetadata.fbp,
          geo: gtmEventMetadata.geo,
          hashedEmail: gtmEventMetadata.hashedEmail,
          inApp: gtmEventMetadata.inApp,
          isAutomatedTest: gtmEventMetadata.isAutomatedTest,
          language: gtmEventMetadata.language,
          pageUrl: gtmEventMetadata.pageUrl,
          sourceTag: gtmEventMetadata.sourceTag,
          targetingCookiesEnabled: gtmEventMetadata.targetingCookiesEnabled,
          timestamp: gtmEventMetadata.timestamp,
          url: gtmEventMetadata.pageUrl,
          userAgent: gtmEventMetadata.userAgent,
          ...(gtmEventMetadata.cjeCookie && {cjData: gtmEventMetadata.cjeCookie}),
          // Subscription Metadata
          currency: gtmEventMetadata.currency,
          firstName: gtmEventMetadata.firstName,
          lastName: gtmEventMetadata.lastName,
          mobile: gtmEventMetadata.mobile,
          product: gtmEventMetadata.product,
          state: gtmEventMetadata.state,
          value: gtmEventMetadata.value,
          zip: gtmEventMetadata.zip,
        }
      }
    );
  }
}

export {
  PixelService
};
